// frontend/src/pages/Company/EditCompany.jsx
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import {
  Building2, Save, Loader2, X, ArrowLeft, Users,
  AlertCircle, Edit, UserPlus, Mail, Trash2, Eye, Key, Shield,
  ChevronLeft, Tag, DollarSign, Globe, Upload, Image as ImageIcon
} from "lucide-react";
import "./CompanyForm.css";

// ✅ Backend base URL — same pattern as Banktype.jsx
const url = process.env.REACT_APP_BACKEND_URL;

// ✅ Helper: build auth header from localStorage token
const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const EditCompany = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null); // ✅ create ref for file input

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [errors, setErrors] = useState({});
  const [showConfirm, setShowConfirm] = useState(false);
  const [focused, setFocused] = useState({});
  const [activeTab, setActiveTab] = useState("company");
  const [editingUser, setEditingUser] = useState(null);
  const [showUserForm, setShowUserForm] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  // Logo states
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [currentLogoPath, setCurrentLogoPath] = useState(null);

  // Dropdown states
  const [dropdowns, setDropdowns] = useState({
    businessTypes: [],
    currencies: [],
    countries: []
  });
  const [loadingDropdowns, setLoadingDropdowns] = useState(false);

  // Company data
  const [companyData, setCompanyData] = useState({
    company_name: "",
    business_type: "",
    base_currency: "",
    fiscal_year_start: new Date().getFullYear() + "-01-01",
    country: "",
    status: "active",
  });

  const [userData, setUserData] = useState({
    Usercomp_sure_name: "",
    Usercomp_contact: "",
    Usercomp_address: "",
    Usercomp_contact_2: "",   // ✅ NEW
    Usercomp_contact_3: "",   // ✅ NEW
    Usercomp_email: "",
    role: "user",
    Usercomp_status: "active"
  });

  const [passwordData, setPasswordData] = useState({
    current_password: "",
    new_password: "",
    confirm_password: ""
  });

  const [originalCompanyData, setOriginalCompanyData] = useState(null);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);

  // Fetch dropdown data
  const fetchDropdowns = async () => {
    try {
      setLoadingDropdowns(true);
      const response = await axios.get(`${url}/api/company/dropdowns/all`, {
        headers: getAuthHeaders(),
      });
      if (response.data.success) {
        setDropdowns({
          businessTypes: response.data.data.business_types || [],
          currencies: response.data.data.currencies || [],
          countries: response.data.data.countries || []
        });
      }
    } catch (error) {
      toast.error("Failed to load dropdown data");
    } finally {
      setLoadingDropdowns(false);
    }
  };

  useEffect(() => {
    fetchCompanyData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchCompanyData = async () => {
    try {
      setLoading(true);
      await fetchDropdowns();

      const res = await axios.get(`${url}/api/company/${id}`, {
        headers: getAuthHeaders(),
      });
      const company = res.data.data;

      const formattedData = {
        company_name: company.company_name || "",
        business_type: company.business_type?._id || "",
        base_currency: company.base_currency?._id || "",
        fiscal_year_start: company.fiscal_year_start
          ? company.fiscal_year_start.split("T")[0]
          : new Date().getFullYear() + "-01-01",
        country: company.country?._id || "",
        status: company.status || "active",
      };

      setCompanyData(formattedData);
      setOriginalCompanyData(formattedData);

      if (company.logo) {
        const logoUrl = `${url}/${company.logo}`;
        setCurrentLogoPath(logoUrl);
        setLogoPreview(logoUrl);
      } else {
        setCurrentLogoPath(null);
        setLogoPreview(null);
      }

      try {
        const usersRes = await axios.get(`${url}/api/company/${id}/users`, {
          headers: getAuthHeaders(),
        });
        if (usersRes.data.success) {
          if (usersRes.data.data && usersRes.data.data.users) {
            setCompanyUsers(usersRes.data.data.users);
          } else if (Array.isArray(usersRes.data.data)) {
            setCompanyUsers(usersRes.data.data);
          }
        }
      } catch (userErr) {
        setCompanyUsers([]);
      }

    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to fetch company details");
      navigate("/ViewCompany");
    } finally {
      setLoading(false);
    }
  };

  const handleCompanyChange = (e) => {
    const { name, value } = e.target;
    const newData = { ...companyData, [name]: value };
    setCompanyData(newData);

    if (originalCompanyData) {
      const isChanged = JSON.stringify(newData) !== JSON.stringify(originalCompanyData);
      setHasChanges(isChanged);
    }

    if (errors[name]) setErrors(prev => ({ ...prev, [name]: "" }));
  };

  const validateCompanyForm = () => {
    const newErrors = {};
    if (!companyData.company_name.trim()) newErrors.company_name = "Company name is required";
    if (!companyData.fiscal_year_start) newErrors.fiscal_year_start = "Fiscal year start is required";
    if (!companyData.business_type) newErrors.business_type = "Business type is required";
    if (!companyData.base_currency) newErrors.base_currency = "Currency is required";
    if (!companyData.country) newErrors.country = "Country is required";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error("Please fix the errors in the form");
      return false;
    }
    setErrors({});
    return true;
  };

  const handleCompanySubmit = async (e) => {
    e.preventDefault();
    if (!validateCompanyForm()) return;

    try {
      setSubmitting(true);
      const updateData = {
        company_name: companyData.company_name,
        business_type: companyData.business_type,
        base_currency: companyData.base_currency,
        fiscal_year_start: companyData.fiscal_year_start,
        country: companyData.country,
        status: companyData.status,
      };
      await axios.put(`${url}/api/company/${id}`, updateData, {
        headers: getAuthHeaders(),
      });
      toast.success("Company updated successfully!");
      setOriginalCompanyData(companyData);
      setHasChanges(false);
      fetchCompanyData();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update company");
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
      if (!validTypes.includes(file.type)) {
        toast.error("Please select a valid image file");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error("File size must be less than 5MB");
        return;
      }
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
    } else {
    }
  };

  const handleLogoUpload = async () => {
    if (!logoFile) {
      toast.error("No file selected");
      return;
    }

    const formData = new FormData();
    formData.append("logo", logoFile);

    try {
      setUploadingLogo(true);
      await axios.put(`${url}/api/company/${id}/logo`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          ...getAuthHeaders(),
        },
      });
      toast.success("Logo uploaded successfully!");
      await fetchCompanyData();
      setLogoFile(null);
      // Reset file input value so same file can be re-selected
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to upload logo");
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleRemoveLogo = async () => {
    if (!window.confirm("Are you sure you want to remove the company logo?")) return;
    try {
      setSubmitting(true);
      await axios.put(`${url}/api/company/${id}`, { logo: null }, {
        headers: getAuthHeaders(),
      });
      toast.success("Logo removed successfully");
      await fetchCompanyData();
      setLogoPreview(null);
      setCurrentLogoPath(null);
      setLogoFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to remove logo");
    } finally {
      setSubmitting(false);
    }
  };

  // User management functions
  const handleEditUser = (user) => {
    setEditingUser(user);
    setUserData({
      Usercomp_sure_name: user.Usercomp_sure_name || "",
      Usercomp_contact: user.Usercomp_contact || "",
      Usercomp_contact_2: user.Usercomp_contact_2 || "",   // ✅ NEW
      Usercomp_contact_3: user.Usercomp_contact_3 || "",   // ✅ NEW
      Usercomp_address: user.Usercomp_address || "",
      Usercomp_email: user.Usercomp_email || "",
      role: user.role || "user",
      Usercomp_status: user.Usercomp_status || "active"
    });
    setShowUserForm(true);
  };

  const handleUserChange = (e) => {
    const { name, value } = e.target;
    setUserData(prev => ({ ...prev, [name]: value }));
  };

  const validateUserForm = () => {
    const newErrors = {};
    if (!userData.Usercomp_sure_name.trim()) newErrors.Usercomp_sure_name = "Full name is required";
    if (!userData.Usercomp_contact.trim()) newErrors.Usercomp_contact = "Contact number is required";
    if (!userData.Usercomp_address.trim()) newErrors.Usercomp_address = "Address is required";
    if (!userData.Usercomp_email.trim()) newErrors.Usercomp_email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(userData.Usercomp_email)) newErrors.Usercomp_email = "Email is invalid";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error("Please fix the errors in the user form");
      return false;
    }
    return true;
  };

  const handleUserSubmit = async (e) => {
    e.preventDefault();
    if (!validateUserForm()) return;
    try {
      setSubmitting(true);
      await axios.put(`${url}/api/usercomp/${editingUser._id}`, userData, {
        headers: getAuthHeaders(),
      });
      toast.success("User updated successfully!");
      fetchCompanyData();
      setShowUserForm(false);
      setEditingUser(null);
      setUserData({
        Usercomp_sure_name: "",
        Usercomp_contact: "",
        Usercomp_address: "",
        Usercomp_email: "",
        role: "user",
        Usercomp_status: "active"
      });
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update user");
    } finally {
      setSubmitting(false);
    }
  };

  const handleResetPassword = (user) => {
    setEditingUser(user);
    setPasswordData({ current_password: "", new_password: "", confirm_password: "" });
    setShowPasswordModal(true);
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({ ...prev, [name]: value }));
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!passwordData.new_password || !passwordData.confirm_password) {
      toast.error("Please fill in both password fields");
      return;
    }
    if (passwordData.new_password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error("Passwords do not match");
      return;
    }
    try {
      setSubmitting(true);
      await axios.put(
        `${url}/api/usercomp/${editingUser._id}/reset-password`,
        { new_password: passwordData.new_password },
        { headers: getAuthHeaders() }
      );
      toast.success("Password reset successfully!");
      setShowPasswordModal(false);
      setPasswordData({ current_password: "", new_password: "", confirm_password: "" });
      setEditingUser(null);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to reset password");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteUser = async (user) => {
    if (!window.confirm(`Are you sure you want to delete user "${user.Usercomp_sure_name}"?`)) return;
    try {
      setSubmitting(true);
      await axios.delete(`${url}/api/usercomp/${user._id}`, {
        headers: getAuthHeaders(),
      });
      toast.success("User deleted successfully!");
      fetchCompanyData();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to delete user");
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddUser = () => navigate(`/company/${id}/add-user`);
  const handleViewUser = (user) => alert(`User Details:\n\nName: ${user.Usercomp_sure_name}\nEmail: ${user.Usercomp_email}\nContact: ${user.Usercomp_contact}\nRole: ${user.role}\nStatus: ${user.Usercomp_status}\nUser Code: ${user.Usercomp_code || "N/A"}\nCreated: ${new Date(user.createdAt).toLocaleDateString()}`);
  const handleCancel = () => hasChanges ? setShowConfirm(true) : navigate(`/company/${id}`);
  const confirmCancel = () => { setShowConfirm(false); navigate(`/company/${id}`); };
  const handleBack = () => {
    if (showUserForm && editingUser) {
      setShowUserForm(false);
      setEditingUser(null);
    } else if (hasChanges) setShowConfirm(true);
    else navigate(`/company/${id}`);
  };
  const handleFocus = (field) => setFocused({ ...focused, [field]: true });
  const handleBlur = (field, value) => setFocused({ ...focused, [field]: value && value.length > 0 });
  const formatDate = (dateString) => {
    if (!dateString) return "Not set";
    try { return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); }
    catch { return "Invalid date"; }
  };
  const handleBackToUsersList = () => {
    setShowUserForm(false);
    setEditingUser(null);
  };
  const getBusinessTypeName = (id) => dropdowns.businessTypes.find(t => t._id === id)?.name || "";
  const getCurrencyName = (id) => {
    const c = dropdowns.currencies.find(c => c._id === id);
    return c ? `${c.symbol || ''} ${c.name}` : "";
  };
  const getCountryName = (id) => dropdowns.countries.find(c => c._id === id)?.name || "";

  if (loading) {
    return (
      <div className="form-wrapper">
        <div className="company-form-container">
          <div className="loading-container">
            <Loader2 className="animate-spin" size={32} />
            <p>Loading company data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="form-wrapper">
      <div className="company-form-container">
        {/* Close button */}
        <button className="close-btn" onClick={handleBack} title="Close"><X size={20} /></button>

        {/* Confirmation Modal */}
        {showConfirm && (
          <div className="confirmation-modal">
            <div className="modal-content">
              <div className="modal-header"><AlertCircle size={24} color="#f89a3a" /><h3>Unsaved Changes</h3></div>
              <div className="modal-body"><p>You have unsaved changes. Are you sure you want to leave?</p></div>
              <div className="modal-actions">
                <button className="cancel-btn" onClick={() => setShowConfirm(false)}>Stay on Page</button>
                <button className="submit-btn" onClick={confirmCancel}>Leave Anyway</button>
              </div>
            </div>
          </div>
        )}

        {/* Password Reset Modal */}
        {showPasswordModal && (
          <div className="confirmation-modal">
            <div className="modal-content">
              <div className="modal-header"><Key size={24} color="#f89a3a" /><h3>Reset Password for {editingUser?.Usercomp_sure_name}</h3></div>
              <div className="modal-body">
                <form onSubmit={handlePasswordSubmit}>
                  <div className="form-field" style={{ marginBottom: '15px' }}>
                    <input type="password" name="new_password" value={passwordData.new_password} onChange={handlePasswordChange} placeholder="Enter new password" required />
                    <label>New Password</label>
                  </div>
                  <div className="form-field" style={{ marginBottom: '20px' }}>
                    <input type="password" name="confirm_password" value={passwordData.confirm_password} onChange={handlePasswordChange} placeholder="Confirm new password" required />
                    <label>Confirm Password</label>
                  </div>
                </form>
              </div>
              <div className="modal-actions">
                <button className="cancel-btn" onClick={() => setShowPasswordModal(false)} disabled={submitting}>Cancel</button>
                <button className="submit-btn" onClick={handlePasswordSubmit} disabled={submitting}>
                  {submitting ? <Loader2 className="animate-spin mr-2" size={16} /> : null} Reset Password
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Back button */}
        <div style={{ marginBottom: '20px' }}>
          <button onClick={handleBack} style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'none', border: 'none', color: '#121349', fontSize: '16px', fontWeight: '600', cursor: 'pointer', padding: '10px 0' }}>
            <ArrowLeft size={20} /> Back to Company View
          </button>
        </div>

        {/* Header */}
        <div className="form-header">
          <Building2 size={24} />
          <h2 className="form-title">Edit Company & Users</h2>
          <p className="form-subtitle">Manage company information and user accounts for {companyData.company_name}</p>
        </div>

        {/* Main Content */}
        {showUserForm && editingUser ? (
          // User edit form
          <div className="user-edit-form">
            <div className="user-edit-header">
              <button className="back-to-users-btn" onClick={handleBackToUsersList} style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'none', border: 'none', color: '#666', cursor: 'pointer', marginBottom: '20px', fontSize: '14px' }}>
                <ChevronLeft size={16} /> Back to Users List
              </button>
              <div className="form-header" style={{ marginBottom: '20px', textAlign: 'left' }}>
                <Edit size={20} />
                <h3 style={{ margin: '10px 0 5px 0', fontSize: '24px' }}>Edit User: {editingUser.Usercomp_sure_name}</h3>
                <p style={{ color: '#666', fontSize: '14px' }}>Update user information</p>
              </div>
            </div>
            <form onSubmit={handleUserSubmit}>
              <div className="form-grid">
                <div className={`form-field required ${focused.Usercomp_sure_name || userData.Usercomp_sure_name ? "filled" : ''}`}>
                  <input name="Usercomp_sure_name" value={userData.Usercomp_sure_name} onChange={handleUserChange} onFocus={() => handleFocus("Usercomp_sure_name")} onBlur={() => handleBlur("Usercomp_sure_name", userData.Usercomp_sure_name)} placeholder="Enter full name" required />
                  <label>Full Name</label>
                </div>
                <div className={`form-field required ${focused.Usercomp_contact || userData.Usercomp_contact ? "filled" : ''}`}>
  <input name="Usercomp_contact" value={userData.Usercomp_contact} onChange={handleUserChange} onFocus={() => handleFocus("Usercomp_contact")} onBlur={() => handleBlur("Usercomp_contact", userData.Usercomp_contact)} placeholder="Enter primary phone number" required />
  <label>Phone 1 (Primary)</label>
</div>

{/* ✅ NEW: Phone 2 */}
<div className={`form-field ${focused.Usercomp_contact_2 || userData.Usercomp_contact_2 ? "filled" : ''}`}>
  <input name="Usercomp_contact_2" value={userData.Usercomp_contact_2} onChange={handleUserChange} onFocus={() => handleFocus("Usercomp_contact_2")} onBlur={() => handleBlur("Usercomp_contact_2", userData.Usercomp_contact_2)} placeholder="Enter secondary phone number (optional)" />
  <label>Phone 2 (Optional)</label>
</div>

{/* ✅ NEW: Phone 3 */}
<div className={`form-field ${focused.Usercomp_contact_3 || userData.Usercomp_contact_3 ? "filled" : ''}`}>
  <input name="Usercomp_contact_3" value={userData.Usercomp_contact_3} onChange={handleUserChange} onFocus={() => handleFocus("Usercomp_contact_3")} onBlur={() => handleBlur("Usercomp_contact_3", userData.Usercomp_contact_3)} placeholder="Enter tertiary phone number (optional)" />
  <label>Phone 3 (Optional)</label>
</div>
                <div className={`form-field required ${focused.Usercomp_address || userData.Usercomp_address ? "filled" : ''}`}>
                  <textarea name="Usercomp_address" value={userData.Usercomp_address} onChange={handleUserChange} onFocus={() => handleFocus("Usercomp_address")} onBlur={() => handleBlur("Usercomp_address", userData.Usercomp_address)} placeholder="Enter address" rows="3" required />
                  <label>Address</label>
                </div>
                <div className={`form-field required ${focused.Usercomp_email || userData.Usercomp_email ? "filled" : ''}`}>
                  <input type="email" name="Usercomp_email" value={userData.Usercomp_email} onChange={handleUserChange} onFocus={() => handleFocus("Usercomp_email")} onBlur={() => handleBlur("Usercomp_email", userData.Usercomp_email)} placeholder="Enter email" required />
                  <label>Email Address</label>
                </div>
                <div className={`form-field ${focused.role || userData.role ? "filled" : ''}`}>
                  <select name="role" value={userData.role} onChange={handleUserChange} onFocus={() => handleFocus("role")} onBlur={() => handleBlur("role", userData.role)}>
                    <option value="admin">Admin</option><option value="sales">Sales</option><option value="cashier">Cashier</option><option value="purchase">Purchase</option><option value="user">User</option>
                  </select>
                  <label>Role</label>
                </div>
                <div className={`form-field ${focused.Usercomp_status || userData.Usercomp_status ? "filled" : ''}`}>
                  <select name="Usercomp_status" value={userData.Usercomp_status} onChange={handleUserChange} onFocus={() => handleFocus("Usercomp_status")} onBlur={() => handleBlur("Usercomp_status", userData.Usercomp_status)}>
                    <option value="active">Active</option><option value="inactive">Inactive</option>
                  </select>
                  <label>Status</label>
                </div>
              </div>

              <div className="form-actions" style={{ marginTop: '30px' }}>
                <button type="button" className="cancel-btn" onClick={handleBackToUsersList} disabled={submitting}>Cancel</button>
                <button type="submit" className="submit-btn" disabled={submitting}>
                  {submitting ? <><Loader2 className="animate-spin mr-2" size={16} /> Updating...</> : <>Update User <Save className="ml-2" size={16} /></>}
                </button>
              </div>
            </form>
          </div>
        ) : (
          <>
            {/* Tabs */}
            {!showUserForm && (
              <div className="tabs">
                <button className={`tab ${activeTab === "company" ? "active" : ""}`} onClick={() => setActiveTab("company")}><Building2 size={16} className="mr-2" /> Company Details</button>
                <button className={`tab ${activeTab === "users" ? "active" : ""}`} onClick={() => setActiveTab("users")}><Users size={16} className="mr-2" /> Users ({companyUsers.length})</button>
              </div>
            )}

            {/* Quick Actions */}
            {!showUserForm && (
              <div className="quick-actions">
                <button className="quick-action-btn primary" onClick={handleAddUser}><UserPlus size={16} className="mr-2" /> Add New User</button>
              </div>
            )}

            {/* COMPANY EDIT FORM */}
            {activeTab === "company" && !showUserForm && (
              <form className="edit-form" onSubmit={handleCompanySubmit}>
                <div className="form-grid">
                  {/* Row 1: Company Name (full width) */}
                  <div className={`form-field required ${focused.company_name || companyData.company_name ? "filled" : ""} ${errors.company_name ? 'error' : ''}`} style={{ gridColumn: 'span 2' }}>
                    <input name="company_name" value={companyData.company_name} onChange={handleCompanyChange} onFocus={() => handleFocus("company_name")} onBlur={() => handleBlur("company_name", companyData.company_name)} placeholder="Enter company name" required />
                    <label>Company Name</label>
                    {errors.company_name && <small className="field-error">{errors.company_name}</small>}
                  </div>

                  {/* Row 2: Business Type (col1), Currency (col2) */}
                  <div className={`form-field required ${focused.business_type || companyData.business_type ? "filled" : ""} ${errors.business_type ? 'error' : ''}`}>
                    <div className="select-wrapper">
                      <select name="business_type" value={companyData.business_type} onChange={handleCompanyChange} onFocus={() => handleFocus("business_type")} onBlur={() => handleBlur("business_type", companyData.business_type)} required disabled={loadingDropdowns}>
                        <option value="">Select Business Type</option>
                        {dropdowns.businessTypes.map((type) => (<option key={type._id} value={type._id}>{type.name}</option>))}
                      </select>
                      <Tag size={16} className="select-icon" />
                    </div>
                    <label>Business Type</label>
                    {errors.business_type && <small className="field-error">{errors.business_type}</small>}
                  </div>

                  <div className={`form-field required ${focused.base_currency || companyData.base_currency ? "filled" : ""} ${errors.base_currency ? 'error' : ''}`}>
                    <div className="select-wrapper">
                      <select name="base_currency" value={companyData.base_currency} onChange={handleCompanyChange} onFocus={() => handleFocus("base_currency")} onBlur={() => handleBlur("base_currency", companyData.base_currency)} required disabled={loadingDropdowns}>
                        <option value="">Select Currency</option>
                        {dropdowns.currencies.map((currency) => (<option key={currency._id} value={currency._id}>{currency.symbol ? `${currency.symbol} - ${currency.name}` : currency.name}{currency.code ? ` (${currency.code})` : ''}</option>))}
                      </select>
                      <DollarSign size={16} className="select-icon" />
                    </div>
                    <label>Base Currency</label>
                    {errors.base_currency && <small className="field-error">{errors.base_currency}</small>}
                  </div>

                  {/* Row 3: Fiscal Year Start (col1), Country (col2) */}
                  <div className={`form-field required ${focused.fiscal_year_start || companyData.fiscal_year_start ? "filled" : ""} ${errors.fiscal_year_start ? 'error' : ''}`}>
                    <input type="date" name="fiscal_year_start" value={companyData.fiscal_year_start} onChange={handleCompanyChange} onFocus={() => handleFocus("fiscal_year_start")} onBlur={() => handleBlur("fiscal_year_start", companyData.fiscal_year_start)} required />
                    <label>Fiscal Year Start</label>
                    {errors.fiscal_year_start && <small className="field-error">{errors.fiscal_year_start}</small>}
                  </div>

                  <div className={`form-field required ${focused.country || companyData.country ? "filled" : ""} ${errors.country ? 'error' : ''}`}>
                    <div className="select-wrapper">
                      <select name="country" value={companyData.country} onChange={handleCompanyChange} onFocus={() => handleFocus("country")} onBlur={() => handleBlur("country", companyData.country)} required disabled={loadingDropdowns}>
                        <option value="">Select Country</option>
                        {dropdowns.countries.map((country) => (<option key={country._id} value={country._id}>{country.name} {country.code ? `(${country.code})` : ''}</option>))}
                      </select>
                      <Globe size={16} className="select-icon" />
                    </div>
                    <label>Country</label>
                    {errors.country && <small className="field-error">{errors.country}</small>}
                  </div>

                  {/* Row 4: Status (col1), Logo (col2) */}
                  <div className={`form-field ${focused.status || companyData.status ? "filled" : ""}`}>
                    <select name="status" value={companyData.status} onChange={handleCompanyChange} onFocus={() => handleFocus("status")} onBlur={() => handleBlur("status", companyData.status)}>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                    <label>Status</label>
                  </div>

                  {/* Logo field */}
                  <div className="form-field logo-field">
                    <label>Company Logo</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoChange}
                      style={{ marginBottom: '10px' }}
                    />
                    {logoPreview && <img src={logoPreview} alt="Preview" style={{ width: '80px', height: '80px', objectFit: 'contain' }} />}
                    {logoFile && (
                      <button type="button" onClick={handleLogoUpload} disabled={uploadingLogo} className="submit-btn">
                        {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
                      </button>
                    )}
                  </div>
                </div>

                {/* Change Indicator */}
                {hasChanges && (
                  <div className="change-indicator"><AlertCircle size={16} /><span>You have unsaved changes in company details</span></div>
                )}

                {/* Form Actions */}
                <div className="form-actions">
                  <button type="button" className="cancel-btn" onClick={handleCancel} disabled={submitting}>Cancel</button>
                  <button type="submit" className="submit-btn" disabled={submitting || !hasChanges}>
                    {submitting ? <><Loader2 className="animate-spin mr-2" size={16} /> Updating...</> : <>Update Company <Save className="ml-2" size={16} /></>}
                  </button>
                </div>
              </form>
            )}

            {/* USERS LIST */}
            {activeTab === "users" && !showUserForm && (
              <div className="user-management">
                <div className="users-header">
                  <h3 style={{ color: '#121349', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Users size={20} /> Company Users ({companyUsers.length})
                  </h3>
                </div>

                {companyUsers.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px', background: '#f8f9fa', borderRadius: '10px', border: '1px dashed #ddd' }}>
                    <Users size={48} color="#ccc" />
                    <p style={{ margin: '15px 0', color: '#666', fontSize: '16px' }}>No users found for this company</p>
                    <button className="add-user-btn" onClick={handleAddUser}><UserPlus size={16} className="mr-2" /> Add First User</button>
                  </div>
                ) : (
                  <div className="table-container">
                    <table className="styled-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>User Code</th>
                          <th>Full Name</th>
                          <th>Email</th>
                          <th>Role</th>
                          <th>Status</th>
                          <th>Created</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {companyUsers.map((user, index) => (
                          <tr key={user._id}>
                            <td>{index + 1}</td>
                            <td><span className="company-code" style={{ fontSize: '12px' }}>{user.Usercomp_code || "N/A"}</span></td>
                            <td>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                {user.role === 'admin' && <Shield size={14} color="#f89a3a" />}
                                <strong>{user.Usercomp_sure_name || "N/A"}</strong>
                              </div>
                            </td>
                            <td><div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><Mail size={12} color="#666" /> {user.Usercomp_email || "N/A"}</div></td>
                            <td>
                              <span className={`type-badge ${user.role}`} style={{
                                background: user.role === 'admin' ? 'rgba(248, 154, 58, 0.1)' : user.role === 'sales' ? 'rgba(46, 125, 50, 0.1)' : user.role === 'cashier' ? 'rgba(33, 150, 243, 0.1)' : user.role === 'purchase' ? 'rgba(156, 39, 176, 0.1)' : 'rgba(158, 158, 158, 0.1)',
                                color: user.role === 'admin' ? '#f89a3a' : user.role === 'sales' ? '#2e7d32' : user.role === 'cashier' ? '#2196f3' : user.role === 'purchase' ? '#9c27b0' : '#666',
                                borderColor: user.role === 'admin' ? 'rgba(248, 154, 58, 0.3)' : user.role === 'sales' ? 'rgba(46, 125, 50, 0.3)' : user.role === 'cashier' ? 'rgba(33, 150, 243, 0.3)' : user.role === 'purchase' ? 'rgba(156, 39, 176, 0.3)' : 'rgba(158, 158, 158, 0.3)'
                              }}>
                                {user.role || "user"}
                              </span>
                            </td>
                            <td><span className={`user-status-badge ${user.Usercomp_status}`}>{user.Usercomp_status}</span></td>
                            <td>{formatDate(user.createdAt)}</td>
                            <td>
                              <div className="action-buttons">
                                <button className="action-btn view-btn" onClick={() => handleViewUser(user)} title="View Details"><Eye size={14} /></button>
                                <button className="action-btn edit-btn" onClick={() => handleEditUser(user)} title="Edit User"><Edit size={14} /></button>
                                <button className="action-btn" onClick={() => handleResetPassword(user)} title="Reset Password" style={{ color: '#2196f3', borderColor: 'rgba(33, 150, 243, 0.3)' }}><Key size={14} /></button>
                                {user.role !== 'admin' && (
                                  <button className="action-btn delete-btn" onClick={() => handleDeleteUser(user)} title="Delete User"><Trash2 size={14} /></button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default EditCompany;
